import { useState, useEffect } from 'react';
import { Box, Text, Progress } from '@mantine/core';
import { IconThumbUp, IconThumbDown } from '@tabler/icons-react';
import { formatKNumber, translate } from '@/lib';
import { useItemReaction, type ReactionType } from '@/models/Item';
import { getCategoryName, getGenreName } from '@/models/Metadata';
import { formatRank, calculateLikePercentage, calculateDislikePercentage } from './helpers';
import style from './Style.module.scss';

interface PopularityProps {
  itemId: number;
  likes?: number;
  dislikes?: number;
  reaction?: ReactionType;
  rank?: number;
  categoryRank?: number;
  genreRank?: number;
  categoryId?: number;
  genreId?: number;
  categoryName?: string;
  genreName?: string;
  variant?: 'full' | 'simplified';
}

export function Popularity({ 
  itemId, 
  likes = 0,
  dislikes = 0,
  reaction = 0,
  rank = 0,
  categoryRank = 0,
  genreRank = 0,
  categoryId,
  genreId,
  categoryName: propCategoryName,
  genreName: propGenreName,
  variant = 'full' 
}: PopularityProps) {
  // Local state for optimistic updates
  const [localLikes, setLocalLikes] = useState(likes);
  const [localDislikes, setLocalDislikes] = useState(dislikes);
  const [localReaction, setLocalReaction] = useState<ReactionType>(reaction);

  // Sync with props when they change
  useEffect(() => {
    setLocalLikes(likes);
    setLocalDislikes(dislikes);
    setLocalReaction(reaction);
  }, [likes, dislikes, reaction]);

  // Use the reaction mutation hook
  const { mutate: reactToItem, isPending } = useItemReaction();

  const handleVote = (voteType: 'like' | 'dislike') => {
    if (isPending) return; // Prevent multiple clicks while pending

    const currentReaction = localReaction;
    const isLike = voteType === 'like';
    const targetReaction: ReactionType = isLike ? 1 : 2;

    // Calculate the new reaction type
    let newReaction: ReactionType;
    if (currentReaction === targetReaction) {
      // Clicking the same button - toggle off
      newReaction = 0;
    } else {
      // Clicking a different button or no reaction
      newReaction = targetReaction;
    }

    // Optimistic update
    if (currentReaction === 1) {
      setLocalLikes(prev => prev - 1);
    } else if (currentReaction === 2) {
      setLocalDislikes(prev => prev - 1);
    }

    if (newReaction === 1) {
      setLocalLikes(prev => prev + 1);
    } else if (newReaction === 2) {
      setLocalDislikes(prev => prev + 1);
    }

    setLocalReaction(newReaction);

    // Call the API
    reactToItem(
      { itemId, type: newReaction },
      {
        onError: () => {
          // Revert on error
          setLocalLikes(likes);
          setLocalDislikes(dislikes);
          setLocalReaction(reaction);
        },
      }
    );
  };

  const likePercentage = calculateLikePercentage(localLikes, localDislikes);
  const dislikePercentage = calculateDislikePercentage(localLikes, localDislikes);

  // Get display names for rankings
  const categoryDisplay = propCategoryName || (categoryId ? getCategoryName(categoryId) : 'CATEGORY');
  const genreDisplay = propGenreName || (genreId ? getGenreName(genreId) : 'GENRE');

  const userVote = localReaction === 1 ? 'like' : localReaction === 2 ? 'dislike' : null;

  if (variant === 'simplified') {
    return (
      <Box className={style.simplifiedContainer}>
        <div className={style.simplifiedVotes}>
          <div 
            className={style.simplifiedVoteItem}
            onClick={(e) => {
              e.stopPropagation();
              handleVote('like');
            }}
            style={{ cursor: isPending ? 'wait' : 'pointer' }}
          >
            <span className={`${style.simplifiedIcon} ${style.like}`}>
              <IconThumbUp size={14} fill={userVote === 'like' ? 'currentColor' : 'none'} />
            </span>
            <span className={style.simplifiedCount}>{formatKNumber(localLikes)}</span>
          </div>
          <div 
            className={style.simplifiedVoteItem}
            onClick={(e) => {
              e.stopPropagation();
              handleVote('dislike');
            }}
            style={{ cursor: isPending ? 'wait' : 'pointer' }}
          >
            <span className={`${style.simplifiedIcon} ${style.dislike}`}>
              <IconThumbDown size={14} fill={userVote === 'dislike' ? 'currentColor' : 'none'} />
            </span>
            <span className={style.simplifiedCount}>{formatKNumber(localDislikes)}</span>
          </div>
        </div>
        <Progress.Root size="xs" className={style.simplifiedProgress}>
          <Progress.Section value={likePercentage} color="green" />
          <Progress.Section value={dislikePercentage} color="red" />
        </Progress.Root>
      </Box>
    );
  }

  return (
    <Box className={style.popularityContainer}>
      <Text className={style.header}>- {translate('ff.popularity.title', 'POPULARITY')} -</Text>
      
      {/* Votes Section */}
      <div className={style.votesContainer}>
        <div className={style.voteItem}>
          <div 
            className={`${style.likeIcon} ${userVote === 'like' ? style.active : ''}`}
            onClick={() => handleVote('like')}
            style={{ cursor: isPending ? 'wait' : 'pointer' }}
          >
            <IconThumbUp size={24} fill={userVote === 'like' ? 'currentColor' : 'none'} />
          </div>
          <Text className={style.voteCount}>{formatKNumber(localLikes)}</Text>
        </div>
        
        <div className={style.voteItem}>
          <div 
            className={`${style.dislikeIcon} ${userVote === 'dislike' ? style.active : ''}`}
            onClick={() => handleVote('dislike')}
            style={{ cursor: isPending ? 'wait' : 'pointer' }}
          >
            <IconThumbDown size={24} fill={userVote === 'dislike' ? 'currentColor' : 'none'} />
          </div>
          <Text className={style.voteCount}>{formatKNumber(localDislikes)}</Text>
        </div>
      </div>

      {/* Progress Bar */}
      <div className={style.progressContainer}>
        <Progress.Root size="md">
          <Progress.Section value={likePercentage} color="green" />
          <Progress.Section value={dislikePercentage} color="red" />
        </Progress.Root>
      </div>

      {/* Rankings Grid */}
      <div className={style.rankingsGrid}>
        <div className={style.rankBox}>
          <Text className={style.rankNumber}>{formatRank(genreRank)}</Text>
          <Text className={style.rankLabel}>{translate('ff.popularity.among', 'AMONG')} {genreDisplay.toUpperCase()}</Text>
        </div>
        <div className={style.rankBox}>
          <Text className={style.rankNumber}>{formatRank(categoryRank)}</Text>
          <Text className={style.rankLabel}>{translate('ff.popularity.among', 'AMONG')} {categoryDisplay.toUpperCase()}</Text>
        </div>
        <div className={`${style.rankBox} ${style.rankBoxFull}`}>
          <Text className={style.rankNumber}>{formatRank(rank)}</Text>
          <Text className={style.rankLabel}>{translate('ff.popularity.among', 'AMONG')} {translate('ff.popularity.allItems', 'ALL ITEMS')}</Text>
        </div>
      </div>
    </Box>
  );
}
